import React from 'react'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import {
  AnimatedCheckMark,
  Button,
  Dialog,
  Div,
  Flex,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'

// @src imports
import {
  getDetailedCountries,
  getSponsorBySlug,
  MinimalSponsorFragment,
} from 'src/graphql'
import { Button as ChromeButton } from 'src/chrome'

import {
  useAccount,
  useActions,
  useCallback,
  useDebounce,
  useEffect,
  useMutations,
  usePersistedUserData,
  useQueries,
  useQueryFutures,
  useQueryParams,
  useState,
  useUpdateAccount,
  useVertical,
} from 'src/hooks'

import styles from '../styles/account.module.scss'
import SponsorPicker from './SponsorPicker'
import SponsorBadge from './SponsorBadge'
import { LoginJoinForgot } from 'src/user/components/JoinModal/JoinModal'

import { parsePhoneNumber } from 'libphonenumber-js'
import { formatCountriesAndRegions } from 'src/helpers/formatCountriesAndRegions'
import { ButtonColorChoices } from 'src/theme/buttonColors'

const threeUp: React.CSSProperties = {
  width: '33.333%',
  marginRight: 15,
}

interface Props {
  shouldSetupShippingAddress: boolean
  shouldSetupSponsor: boolean
  onToggleJoinLogin?: (name: LoginJoinForgot) => void
}

const UserProfileForm: React.FC<Props> = props => {
  const {
    shouldSetupShippingAddress,
    shouldSetupSponsor,
    onToggleJoinLogin,
  } = props

  const { phoneVerification } = usePersistedUserData()
  const account = useAccount()
  const actions = useActions()
  const updateAccount = useUpdateAccount()
  const vertical = useVertical()

  const queryParams = useQueryParams()
  const [persistedSponsor] = useQueryFutures(
    queryParams.sponsor
      ? getSponsorBySlug({ slug: queryParams.sponsor })
      : undefined,
  )

  const [detailedCountries] = useQueries(getDetailedCountries())
  const { countries } = formatCountriesAndRegions(detailedCountries)

  const [details, setDetails] = useState({
    email: account.email ?? '',
    username: account.username ?? '',
    password: '',
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    phoneNumber: account.phoneNumber ?? '',
  })

  const [selectedSponsor, setSelectedSponsor] = useState<
    MinimalSponsorFragment | undefined
  >(undefined)
  const clearSponsorCallback = useCallback(
    () => setSelectedSponsor(undefined),
    [],
  )

  const [sponsor, clearSponsor] = selectedSponsor
    ? [selectedSponsor, clearSponsorCallback]
    : [account.sponsor || persistedSponsor?.value, undefined]

  const [shippingAddress, setShippingAddress] = useState(
    account && account.shippingAddress
      ? account.shippingAddress
      : {
          address1: '',
          address2: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'United States',
          company: '',
        },
  )

  const [isUserNameTaken, setIsUserNameTaken] = useState(false)
  const [isSaving, setIsSaving] = useState(0)
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false)
  const [isPickingSponsor, setIsPickingSponsor] = useState(false)

  const mutations = useMutations()

  useEffect(() => {
    setIsUserNameTaken(false)
  }, [details.username])

  const debouncedUsername = useDebounce(details.username, 500)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let mutableCanceling = false
    const isUsernamePristine = account && debouncedUsername === account.username

    if (debouncedUsername && !isUsernamePristine) {
      const validate = async () => {
        const {
          validateUsername: { valid: isValid },
        } = await mutations.validateUsername({ username: debouncedUsername })
        if (mutableCanceling) {
          return
        }
        setIsUserNameTaken(!isValid)
      }
      validate()
    }

    return () => {
      mutableCanceling = true
    }
  }, [debouncedUsername, account, mutations])

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      const sanitizedValue =
        name === 'username'
          ? value.replace(/[^a-z0-9_\-@.+]|\s+/gim, '')
          : value
      setDetails({
        ...details,
        [name]: sanitizedValue,
      })
    },
    [details],
  )
  const isPhoneInvalid = details.phoneNumber.search(/[^0-9 ()+-]+/) !== -1
  const isFormInvalid = isPhoneInvalid

  const onChangeAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      setShippingAddress({
        ...shippingAddress,
        [name]: value,
      })
    },
    [shippingAddress],
  )

  const onChangeCountry = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShippingAddress({
        ...shippingAddress,
        country: e.target.value,
      })
    },
    [shippingAddress],
  )

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setIsSaving(x => x + 1)

    const userInfoUpdate = {
      ...details,
      sponsor: sponsor?.id,
      ...(shouldSetupShippingAddress
        ? {
            ...shippingAddress,
            firstName: details.firstName,
            lastName: details.lastName,
          }
        : {}),
    }

    try {
      await updateAccount(userInfoUpdate, account ? false : true)
      setDetails(x => ({ ...x, password: '' }))
      setIsUpdateSuccessful(true)
    } finally {
      setIsSaving(x => x - 1)
    }
  }

  useEffect(() => {
    const dialogTimeout = setTimeout(() => {
      setIsUpdateSuccessful(false)
    }, 3000)

    return () => {
      clearTimeout(dialogTimeout)
    }
  }, [isUpdateSuccessful, setIsUpdateSuccessful])

  const accountReady = account && account.username

  return (
    <form className={styles.userForm} onSubmit={onSubmit}>
      <Dialog isOpen={isUpdateSuccessful}>
        <Div outset={{ horizontal: 'auto' }}>
          <AnimatedCheckMark size="large" filled={true} />
        </Div>
        <Spacer space="x2" />
        <Text type="largeBody" alignment="center">
          Account updated succesfully
        </Text>
      </Dialog>
      <div style={{ width: '100%' }}>
        {shouldSetupShippingAddress && (
          <div style={{ width: '100%' }}>
            <div>
              <div className={styles.oneField}>
                <TextField
                  name={'address1'}
                  label="Address 1"
                  value={shippingAddress.address1}
                  placeholder={'Address 1'}
                  onChange={onChangeAddress}
                  fullWidth={true}
                  required={true}
                />
              </div>
              <div className={styles.oneField}>
                <TextField
                  name={'address2'}
                  label="Address 2"
                  value={shippingAddress.address2}
                  placeholder={'Address 2'}
                  onChange={onChangeAddress}
                  fullWidth={true}
                  required={false}
                />
              </div>
              <div className={styles.twoFields}>
                <TextField
                  name={'city'}
                  label="City"
                  value={shippingAddress.city}
                  placeholder={'City'}
                  required={true}
                  onChange={onChangeAddress}
                  style={threeUp}
                />
                <TextField
                  name={'state'}
                  label="State"
                  value={shippingAddress.state}
                  placeholder={'State'}
                  required={true}
                  onChange={onChangeAddress}
                  style={threeUp}
                />
                <TextField
                  name={'postalCode'}
                  label="Postal Code"
                  value={shippingAddress.postalCode}
                  placeholder={'Postal Code'}
                  required={true}
                  onChange={onChangeAddress}
                  style={{ width: '33.333%' }}
                />
              </div>
              <div className={styles.twoFields}>
                <TextField
                  name="country"
                  label="Country"
                  value={shippingAddress.country}
                  fullWidth={true}
                  onChange={onChangeCountry}
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {countries &&
                    countries.map((country, index) => {
                      return (
                        <MenuItem key={index} value={country}>
                          {country}
                        </MenuItem>
                      )
                    })}
                </TextField>
              </div>
            </div>
          </div>
        )}
        <div style={{ width: '100%' }}>
          <div className={styles.twoFields}>
            <TextField
              name={'firstName'}
              label="First Name"
              value={details.firstName}
              placeholder={'First Name'}
              required={true}
              onChange={onChange}
            />

            <TextField
              name={'lastName'}
              label="Last Name"
              value={details.lastName}
              placeholder={'Last Name'}
              required={true}
              onChange={onChange}
            />
          </div>
        </div>
        <div className={styles.oneField} style={{ position: 'relative' }}>
          <TextField
            name={'phoneNumber'}
            label="Phone"
            value={details.phoneNumber}
            placeholder={'Phone'}
            fullWidth={true}
            required={account && account.isAffiliate}
            type={'phone'}
            onChange={onChange}
            error={isPhoneInvalid}
            helperText={
              isPhoneInvalid
                ? 'Phone numbers may only be numbers, spaces, and special characters: + ( ) -'
                : null
            }
          />
          {account &&
          !account.verifiedPhoneNumber?.nationalNumber &&
          details.phoneNumber !== '' ? (
            <ChromeButton
              buttonColor={vertical.id as ButtonColorChoices}
              blinking={phoneVerification.unverifiedAccountsFound}
              type={'button'}
              id={'verify_acct_button'}
              style={{
                marginTop: '1rem',
                position: 'absolute',
                top: 0,
                right: 0,
              }}
              title={'Verify My Account'}
              onClick={() => {
                const tempNum = parsePhoneNumber(details.phoneNumber, 'US')
                actions.setUnverifiedAccountsFound(false)
                actions.openPhoneNumberVerification(
                  {
                    countryIsoCode: tempNum.country ? tempNum.country : 'US',
                    nationalNumber: tempNum.nationalNumber.toString(),
                  },
                  false,
                )
              }}
            />
          ) : (
            <ChromeButton
              disabled={true}
              type={'button'}
              id={'verify_acct_button'}
              style={{
                color: '#808080',
                background: '#808080',
                marginTop: '1rem',
                position: 'absolute',
                top: 0,
                right: 0,
              }}
              title={'Verify My Account'}
            />
          )}
        </div>
        <div className={styles.oneField}>
          <TextField
            name={'email'}
            label="Email"
            value={details.email}
            placeholder={'Email'}
            onChange={onChange}
            fullWidth={true}
            required={true}
            type={'email'}
          />
        </div>
        <div className={styles.oneField}>
          <TextField
            name={'username'}
            value={details.username}
            label="Username"
            error={isUserNameTaken}
            helperText={isUserNameTaken ? 'This username is taken' : null}
            placeholder={'Username'}
            onChange={onChange}
            fullWidth={true}
            required={true}
          />
        </div>
        <div className={styles.oneField}>
          <TextField
            name={'password'}
            label={accountReady ? 'Change Password' : 'Password'}
            value={details.password}
            placeholder={accountReady ? 'New Password' : 'Password'}
            onChange={onChange}
            fullWidth={true}
            type={'password'}
            required={!accountReady}
          />
        </div>
        {shouldSetupSponsor ? (
          sponsor && sponsor.id !== '84109' ? (
            <SponsorBadge onClear={clearSponsor} sponsor={sponsor} />
          ) : (
            <SponsorPicker
              onSelect={setSelectedSponsor}
              setPickingState={setIsPickingSponsor}
            />
          )
        ) : null}
      </div>
      <Flex width="100%" flexDirection="column">
        <Spacer space="x2" />
        <Div width="100%">
          <Button
            fullWidth={true}
            type={'secondary'}
            onClick="submitForm"
            title={
              isSaving
                ? 'Saving...'
                : account && account.username
                ? 'Save'
                : 'Join'
            }
            disabled={
              isSaving > 0 || (!sponsor && isPickingSponsor) || isFormInvalid
            }
          />
        </Div>
        <Spacer space="x1" />
        {onToggleJoinLogin && (
          <Button
            hover={true}
            type="secondary"
            outlined={true}
            onClick={() => onToggleJoinLogin?.('login')}
            title="Return to Login"
          />
        )}
      </Flex>
    </form>
  )
}

export default UserProfileForm
